import { useCallback } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { produce } from 'immer'

import { getTokenVisibilityQueryKey, useActiveOrgId } from '~/hooks/org'
import { NFTVisibilityEntry, TokenVisibilityEntry } from '~/types'
import { Address } from '~/utils/address'

type TokenVisibilityData = {
  visibilityEntries: {
    tokens: TokenVisibilityEntry[]
    nfts: NFTVisibilityEntry[]
  }
}

export const useTokenVisibilityUpdates = () => {
  const queryClient = useQueryClient()
  const activeOrgId = useActiveOrgId()

  const updateTokenVisibility = useCallback(
    (updater: (data: TokenVisibilityData) => TokenVisibilityData) => {
      queryClient.setQueryData<TokenVisibilityData>(
        getTokenVisibilityQueryKey(activeOrgId),
        (data) => {
          if (!data) return data
          return updater(data)
        },
      )
    },
    [queryClient, activeOrgId],
  )

  return {
    hideToken: useCallback(
      (token: Omit<TokenVisibilityEntry, 'visible'>) => {
        updateTokenVisibility((data) =>
          produce(data, (draft) => {
            const existingEntry = draft.visibilityEntries.tokens.find(
              (v) =>
                v.chainId === token.chainId &&
                Address.equal(v.address, token.address),
            )

            if (!existingEntry) {
              draft.visibilityEntries.tokens.push({ ...token, visible: false })
            } else {
              existingEntry.visible = false
            }
          }),
        )
      },
      [updateTokenVisibility],
    ),
    unhideToken: useCallback(
      (token: Omit<TokenVisibilityEntry, 'visible'>) => {
        updateTokenVisibility((data) =>
          produce(data, (draft) => {
            const existingEntry = draft.visibilityEntries.tokens.find(
              (v) =>
                v.chainId === token.chainId &&
                Address.equal(v.address, token.address),
            )

            if (!existingEntry) {
              draft.visibilityEntries.tokens.push({ ...token, visible: true })
            } else {
              existingEntry.visible = true
            }
          }),
        )
      },
      [updateTokenVisibility],
    ),
    hideNFT: useCallback(
      (nft: Omit<NFTVisibilityEntry, 'visible'>) => {
        updateTokenVisibility((data) =>
          produce(data, (draft) => {
            const existingEntry = draft.visibilityEntries.nfts.find(
              (v) =>
                v.chainId === nft.chainId &&
                Address.equal(v.address, nft.address) &&
                v.tokenId === nft.tokenId &&
                v.blockType === nft.blockType,
            )

            if (!existingEntry) {
              draft.visibilityEntries.nfts.push({ ...nft, visible: false })
            } else {
              existingEntry.visible = false
            }
          }),
        )
      },
      [updateTokenVisibility],
    ),
    unhideNFT: useCallback(
      (nft: Omit<NFTVisibilityEntry, 'visible'>) => {
        updateTokenVisibility((data) =>
          produce(data, (draft) => {
            const existingEntry = draft.visibilityEntries.nfts.find(
              (v) =>
                v.chainId === nft.chainId &&
                Address.equal(v.address, nft.address) &&
                v.tokenId === nft.tokenId &&
                v.blockType === nft.blockType,
            )

            if (!existingEntry) {
              draft.visibilityEntries.nfts.push({ ...nft, visible: true })
            } else {
              existingEntry.visible = true
            }
          }),
        )
      },
      [updateTokenVisibility],
    ),
  }
}
